// fallback value for empty cells in the Excel sheet
export const FALLBACK_VALUE = '-';

// Row index where actual data starts in the Excel sheet
//   - row 0: indicates which table sections are "RESULTS" vs "IDENTIFIERS"
//   - row 1: contains the headers for the sheet
//   - row 2 onward: actual data rows
export const DATA_START_ROW_INDEX = 2;

export type ResultsViewerProps = {
  fileBuffer?: ArrayBuffer | Uint8Array | null;
};

export type SheetTabsProps = {
  sheetNames: string[];
  selectedSheet: string;
  onChange: (name: string) => void;
};

export type DataTableProps = {
  headers: (string | number)[];
  rows: (string | number)[][];
  partnerIndex: Record<number, number>;
};

export type TableData = {
  headers: (string | number)[];
  rows: (string | number)[][];
  partnerIndex: Record<number, number>;
  label?: string;
};

/**
 * Computes a new column ordering and partner index mapping for Excel sheet headers.
 *
 * Reorders columns by:
 *  - Keeping fixed leading columns (like "id", "Source file position") first in the given order.
 *  - Grouping pairs of columns with "(new)" and "(existing)" suffixes by their base name,
 *    sorted alphabetically.
 *  - Appending all other columns in their original order.
 *
 * Also returns a partnerIndex map, where each "new" column points to its "existing" pair and vice versa.
 *
 * @param rawHeaders - The list of headers as parsed from the sheet.
 * @param fixedLeadingLabels - Optional list of labels that must always appear first.
 * @returns An object with:
 *   - orderedIndices: indices of headers in the new order.
 *   - partnerIndex: mapping between paired column positions.
 */
const computeOrdering = (
  rawHeaders: (string | number)[],
  fixedLeadingLabels: string[] = ['id', 'Source file position'],
): { orderedIndices: number[]; partnerIndex: Record<number, number> } => {
  const fixedLeadingIdx: number[] = [];
  const pairs: Record<string, { new?: number; existing?: number }> = {};

  for (let i = 0; i < rawHeaders.length; i++) {
    const label = String(rawHeaders[i]!);
    const leadPos = fixedLeadingLabels.findIndex((l) => l.toLowerCase() === label.toLowerCase());
    if (leadPos !== -1) continue; // handled later in declared order

    const m = label.match(/^(.*)\s\((new|existing)\)$/i);
    if (m) {
      const base = m[1]!.trim();
      const kind = m[2]!.toLowerCase() as 'new' | 'existing';
      if (!pairs[base]) pairs[base] = {};
      pairs[base][kind] = i;
    }
  }

  fixedLeadingLabels.forEach((fl) => {
    const idx = rawHeaders.findIndex((h) => String(h).toLowerCase() === fl.toLowerCase());
    if (idx !== -1) fixedLeadingIdx.push(idx);
  });

  const sortedBases = Object.keys(pairs).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );

  const orderedIndices: number[] = [];
  orderedIndices.push(...fixedLeadingIdx);
  for (const base of sortedBases) {
    const p = pairs[base];
    if (p?.new !== undefined) orderedIndices.push(p.new);
    if (p?.existing !== undefined) orderedIndices.push(p.existing);
  }

  for (let i = 0; i < rawHeaders.length; i++) {
    if (!orderedIndices.includes(i)) orderedIndices.push(i);
  }

  const partnerIndex: Record<number, number> = {};
  for (const base of sortedBases) {
    const p = pairs[base];
    if (p?.new !== undefined && p?.existing !== undefined) {
      const ordNew = orderedIndices.indexOf(p.new);
      const ordExisting = orderedIndices.indexOf(p.existing);
      if (ordNew !== -1 && ordExisting !== -1) {
        partnerIndex[ordNew] = ordExisting;
        partnerIndex[ordExisting] = ordNew;
      }
    }
  }

  return { orderedIndices, partnerIndex };
};

/**
 * Reorders the headers and body rows of a sheet according to provided column indices.
 *
 * @param json - The raw sheet data as an array of arrays.
 * @param orderedIndices - The indices to use for reordering columns.
 * @returns An object with:
 *   - headers: reordered headers.
 *   - rows: reordered body rows.
 */
const reorderSheet = (
  json: (string | number)[][],
  orderedIndices: number[],
): { headers: (string | number)[]; rows: (string | number)[][] } => {
  const rawHeaders = (json[1] || []) as (string | number)[];
  const headers: (string | number)[] = orderedIndices.map((i) => rawHeaders[i] ?? FALLBACK_VALUE);
  const rows: (string | number)[][] = json
    .slice(DATA_START_ROW_INDEX)
    .map((r = []) => orderedIndices.map((i) => r[i] ?? FALLBACK_VALUE) as (string | number)[]);
  return { headers, rows };
};

/**
 * Parse a single Excel-like table "section" (e.g., RESULTS or BLANKS) into a
 * normalized structure usable by the ResultsViewer.
 *
 * A section is expected to be a 2D array where:
 *  - row 0: meta row (ignored here). In the original sheet this distinguishes
 *           which columns belong to RESULTS vs IDENTIFIERS.
 *  - row 1: header row used as column labels.
 *  - row 2..N: data rows.
 *
 * Behavior:
 *  1) Reorders columns so that fixed leading labels ("id", "Source file position")
 *     stay first, followed by paired columns that share the same base name and end
 *     with "(new)"/"(existing)", sorted alphabetically by base name, and finally
 *     any remaining columns in their original order.
 *  2) Builds a `partnerIndex` map that links the position of each "(new)" column
 *     to its corresponding "(existing)" column (and vice‑versa) **after** the
 *     reordering. If a pair is incomplete, no mapping is created for that base.
 *  3) Uses `FALLBACK_VALUE` for any missing header/cell and starts reading data at
 *     `DATA_START_ROW_INDEX`.
 *  4) When `removeBlankCols` is true, drops columns that are entirely the
 *     fallback value (i.e., all cells are "-"). The `partnerIndex` is updated to
 *     reflect the reduced column set.
 *
 * Notes on inputs and normalization:
 *  - Header matching for fixed leaders and pairs is case‑insensitive.
 *  - Pair detection relies on the pattern: `<base> (new)` or `<base> (existing)`.
 *  - Non‑string headers are coerced to string for comparisons and returned as-is
 *    in the `headers` array.
 *
 * @param section The raw section as a matrix of cells. Must contain at least two rows (meta + headers);
 *    otherwise returns null.
 * @param label A human‑readable label to carry through (e.g., "RESULTS", "BLANKS"). Included in the returned object.
 * @param removeBlankCols When true, columns that are entirely fallback values are removed from the output.
 *    Defaults to false.
 *
 * @returns `TableData | null`
 *  - `headers`: Reordered (and optionally reduced) header labels.
 *  - `rows`: Reordered (and optionally reduced) data rows starting at `DATA_START_ROW_INDEX`.
 *  - `partnerIndex`: Mapping between paired column positions in the returned order (e.g., `{ 5: 6, 6: 5 }`).
 *    Unpaired columns are omitted.
 *  - `label`: The same label passed in.
 *  - Returns `null` if the section does not have at least a meta row and a header row.
 *
 * Example
 * -------
 * const table: (string | number)[][] = [
 *   ["RESULTS", "-", "-"],
 *   ["id", "name (new)", "name (existing)"],
 *   [1, "Foo", "FOO"],
 *   [2, "Bar", "BAR"],
 * ];
 *
 * const parsed = parseSection(table, "RESULTS", true);
 * // parsed.headers -> ["id", "name (new)", "name (existing)"]
 * // parsed.rows    -> [[1, "Foo", "FOO"], [2, "Bar", "BAR"]]
 * // parsed.partnerIndex -> { 1: 2, 2: 1 }
 * // parsed.label   -> "RESULTS"
 */
export const parseSection = (
  section: (string | number)[][],
  label: string,
  removeBlankCols: boolean = false,
): TableData | null => {
  if (!section || section.length < 2) return null;
  const rawHeaders = (section[1] || []) as (string | number)[];
  const { orderedIndices, partnerIndex } = computeOrdering(rawHeaders);
  const { headers, rows } = reorderSheet(section as (string | number)[][], orderedIndices);

  // remove columns that are entirely blanks ('-')
  const validIndices = headers
    .map((h, i) => ({ h, i }))
    .filter(({ h, i }) => {
      if (h !== '-') return true;
      return rows.some((r) => String(r[i]) !== '-');
    })
    .map(({ i }) => i);

  const newHeaders = removeBlankCols ? validIndices.map((i) => headers[i]!) : headers;
  const newRows = removeBlankCols ? rows.map((r) => validIndices.map((i) => r[i]!)) : rows;

  const newPartnerIndex: Record<number, number> = {};
  for (const [key, val] of Object.entries(partnerIndex)) {
    const ki = validIndices.indexOf(Number(key));
    const vi = validIndices.indexOf(val);
    if (ki !== -1 && vi !== -1) newPartnerIndex[ki] = vi;
  }

  return { headers: newHeaders, rows: newRows, partnerIndex: newPartnerIndex, label };
};
